/**
 * Single-line `Text` entity → `RenderableEntity` adapter (TYPED).
 *
 * DEFUSE Direction-3 — typed sibling of `../textToRenderable.ts`. Reads the
 * canonical `TextEntity` fields directly off the model (`model.ts`:
 * `textEntitySchema` insertionPoint / height / rotation / widthFactor /
 * obliqueAngle / styleName / contents / hJustification / vJustification)
 * instead of the legacy `EntityPreview.properties` string bag. Produces a
 * byte-identical `RenderableEntity` (golden-parity) for the same entity.
 *
 * # Golden-parity quirks reproduced EXACTLY (not "better")
 *
 *   - Insertion z: `textEntitySchema.insertionPoint` is the 2D
 *     `drawingPointSchema` (no z). The producer `buildTextPreview`
 *     (`dxfGeometry.ts`) writes ONLY `Insertion X` / `Insertion Y` to the
 *     bag — never `Insertion Z` — so legacy `readNumber(.., "Insertion Z")`
 *     resolves to 0. This adapter feeds `z: 0` to the builder for identical
 *     output. (No z-superset divergence exists here: the model itself carries
 *     no insertion z.)
 *   - Height: legacy reads `readPositiveNumber(props, "Height", 2.5)` — a
 *     non-positive height (e.g. 0 for annotative TEXT, which `height` permits
 *     via `z.number().nonnegative()`) falls back to 2.5. This adapter applies
 *     the SAME positive-or-2.5 clamp to `entity.height`.
 *   - Width factor: legacy `readPositiveNumber(props, "Width factor", 1)` →
 *     a non-positive width factor falls back to 1. Same clamp here.
 *   - Justify: the bag's `Justify` is `justifyFromCodes(hJust, vJust)`
 *     (`dxfGeometry.ts`); this adapter derives the SAME keyword from the typed
 *     `hJustification` / `vJustification` codes via the SAME exported helper.
 *   - hasAlignmentPoint: `buildTextPreview` ALWAYS writes `Alignment X` /
 *     `Alignment Y` (defaulting to the insertion point when the entity has no
 *     alignment point), so legacy's `hasAlignmentPoint` is always true on this
 *     path. This adapter passes `true` to match.
 *   - styleName / content: the bag carries `entity.styleName ?? "Standard"`
 *     and `entity.contents` verbatim; the legacy adapter then applies
 *     `(styleName ?? "Standard").trim() || "Standard"` and
 *     `Contents ?? label ?? ""`. Since `buildTextPreview` always writes a
 *     `Contents` string, the typed content is `entity.contents`.
 *
 * Category B: standalone file; NOT imported into any render path yet.
 */

import type { TextEntity } from "@dxf-ai-editor/drawing-model";

import { justifyFromCodes } from "../../../state/dxfGeometry";
import type { RenderableEntity, Vec3 } from "../../types";

import {
  buildRenderableShell,
  optionalPointZ,
  positiveOr,
  type AdapterContext,
} from "../adapterContext";
import { buildTextSceneGeometry } from "../geom/textGeometry";
import { resolveTypedAnnotation } from "./annotativeResolve";

/**
 * Typed adapter for the `Text` entity (`textEntitySchema`).
 *
 * # S10d — annotative per-scale representation
 *
 * When the entity is annotative and a representation matches the active
 * CANNOSCALE, its per-scale insertion / rotation override the master baseline
 * and the height renders at `paper_height × effectiveScale` model units
 * (mirrors `renderAttribute.tsx`). VISIBILITY (hiding an annotative TEXT that
 * has NO representation at the active scale when ANNOALLVISIBLE=false) is gated
 * one level up in `renderableFromDrawingEntity` (the single live typed entry),
 * so this adapter keeps its non-null return — a hidden entity never reaches it.
 */
export function textFromEntity(
  entity: TextEntity,
  ctx: AdapterContext,
): RenderableEntity {
  // S10d — resolve the active-scale annotative representation (single truth:
  // `resolveCannoscaleContext`). Non-annotative TEXT / no active scale →
  // `effectiveScale: 1`, `activeContext: null`, so the overrides below are
  // no-ops (golden parity intact).
  const anno = resolveTypedAnnotation(entity.annotativeContexts, ctx);

  const insRaw: Vec3 = {
    // Per-scale insertion overrides the master baseline when a representation
    // matched (`anno.insertion` present only for a matched text context).
    x: anno.insertion?.x ?? entity.insertionPoint.x,
    y: anno.insertion?.y ?? entity.insertionPoint.y,
    // `insertionPoint` is 2D on the schema → a top-level entity reads 0
    // (legacy bag has no `Insertion Z` key — golden parity intact). A
    // block-flatten clone carries a REAL transformed z (@codex #431 P2 — Z
    // preserve): read it via the shared `optionalPointZ` so text-like
    // children lift with their Line/Circle/Spline siblings.
    z: optionalPointZ(entity.insertionPoint),
  };

  // Match legacy `readPositiveNumber(props, "Height", 2.5)` — a non-positive
  // (e.g. annotative 0) height falls back to 2.5. Same positive-or-default
  // clamp for the width factor. `positiveOr` is the shared one-truth. An
  // annotative TEXT renders at paper_height × effectiveScale model units
  // (mirrors `renderAttribute.tsx`'s `baseHeight * annoCtx.effectiveScale`);
  // `effectiveScale` is 1 for the non-annotative / no-match case.
  const height = positiveOr(entity.height, 2.5) * anno.effectiveScale;
  const rotation = anno.rotation ?? (Number.isFinite(entity.rotation) ? entity.rotation : 0);
  const widthFactor = positiveOr(entity.widthFactor, 1);
  const oblique = Number.isFinite(entity.obliqueAngle)
    ? entity.obliqueAngle
    : 0;
  const styleName = (entity.styleName ?? "Standard").trim() || "Standard";
  const content = entity.contents ?? "";
  // The bag's `Justify` is derived from the 72/73 codes by `justifyFromCodes`;
  // reuse the SAME helper so the keyword string is byte-identical.
  const justify = justifyFromCodes(
    entity.hJustification ?? 0,
    entity.vJustification ?? 0,
  );
  // `buildTextPreview` always writes `Alignment X/Y`, so legacy's
  // `hasAlignmentPoint` is always true on this path.
  const hasAlignmentPoint = true;
  // Codex #342 F1 — group-71 generation flags (the bag carries `Generation
  // flags`; the typed entity carries the same numeric field). Same flip semantics
  // on both paths so the parity test stays byte-identical for non-mirrored text.
  const generationFlags = entity.generationFlags ?? 0;

  const { geometry, bounds } = buildTextSceneGeometry(
    insRaw,
    height,
    rotation,
    widthFactor,
    oblique,
    styleName,
    content,
    justify,
    hasAlignmentPoint,
    generationFlags,
  );

  return buildRenderableShell(entity, ctx, geometry, bounds);
}
